import { Flex, Text, IconButton, Icon, Button } from '@nula/core-ui';
import { useAudioPlayback, type PlaybackUrlFetcher } from './use-audio-playback';
import { TranscriptScrubber } from './transcript-scrubber';
import { formatElapsed } from './elapsed';
import styles from './transcript-player.module.css';

export interface TranscriptPlayerProps {
  recordingId: string;
  /** Segment start times (ms) — drawn as tick marks on the scrubber. */
  boundariesMs: number[];
  durationMs?: number;
  /** Share path: fetch the playback URL token-scoped instead of via the
   * authenticated recordings route. */
  fetchPlaybackUrl?: PlaybackUrlFetcher;
  className?: string;
}

/**
 * S1 squircle audio player for the transcript top bar: play/pause + a
 * segment-ticked scrubber + current/total time + speed, all in a bordered
 * squircle that matches the search field. Drives the shared useAudioPlayback
 * engine; the transcript stream highlights/auto-scrolls via the playhead events
 * the engine dispatches.
 */
export function TranscriptPlayer({ recordingId, boundariesMs, durationMs, fetchPlaybackUrl, className }: TranscriptPlayerProps) {
  const {
    isPlaying,
    currentTimeMs,
    totalMs,
    speed,
    togglePlay,
    cycleSpeed,
    seekToMs,
    audioElement,
  } = useAudioPlayback(recordingId, durationMs, { fetchPlaybackUrl });

  return (
    <Flex align="center" gap="xs" className={className ? `${styles.player} ${className}` : styles.player}>
      <IconButton
        size="sm"
        aria-label={isPlaying ? 'Pause' : 'Play'}
        onClick={() => void togglePlay()}
        className={styles.playBtn}
      >
        <Icon name={isPlaying ? 'pause' : 'play_arrow'} size={14} />
      </IconButton>

      <Text className={styles.time}>{formatElapsed(currentTimeMs)}</Text>

      <TranscriptScrubber
        positionMs={currentTimeMs}
        durationMs={totalMs}
        boundariesMs={boundariesMs}
        onSeek={seekToMs}
        className={styles.scrubber}
      />

      <Text className={styles.time}>{formatElapsed(totalMs)}</Text>

      <Button
        size="sm"
        variant="text"
        aria-label="Playback speed"
        onClick={cycleSpeed}
        className={styles.speed}
      >
        {speed}×
      </Button>

      {audioElement}
    </Flex>
  );
}
